import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaGraduationCap, FaCertificate, FaGlobe, FaArrowRight, FaDownload, FaCheckCircle, FaBriefcase, FaCode, FaCloud, FaDatabase, FaNetworkWired, FaTools, FaBuilding, FaUserCheck } from 'react-icons/fa';
import HrRoleMatcher from '../components/HrRoleMatcher';

const HeroContainer = styled.section`
  position: relative;
  min-height: 85vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 80px 24px 60px 24px;
  background: radial-gradient(circle at 50% 20%, rgba(69, 170, 242, 0.15) 0%, rgba(7, 19, 37, 1) 70%);
  text-align: center;
  overflow: hidden;
`;

const MetricsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 1100px;
  margin: -40px auto 60px auto;
  position: relative;
  z-index: 10;
  padding: 0 24px;
`;

const MetricCard = styled.div`
  background: rgba(16, 37, 66, 0.85);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(69, 170, 242, 0.3);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
`;

const ModeToggleButton = styled.button`
  padding: 10px 24px;
  border-radius: 30px;
  border: ${props => props.active ? '2px solid #45aaf2' : '1px solid rgba(255,255,255,0.2)'};
  background: ${props => props.active ? 'rgba(69, 170, 242, 0.25)' : 'rgba(255,255,255,0.05)'};
  color: ${props => props.active ? '#ffffff' : '#94a3b8'};
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #ffffff;
    border-color: #45aaf2;
  }
`;

function HomePage() {
  const [viewMode, setViewMode] = useState('hr'); // 'hr' or 'client'

  return (
    <div style={{ backgroundColor: '#071325', minHeight: '100vh', color: '#ffffff' }}>

      {/* HERO SECTION */}
      <HeroContainer>
        {/* Mode Switcher Banner */}
        <div style={{
          display: 'inline-flex',
          gap: '8px',
          background: 'rgba(11, 31, 58, 0.9)',
          padding: '6px',
          borderRadius: '36px',
          border: '1px solid rgba(69, 170, 242, 0.3)',
          marginBottom: '32px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          <ModeToggleButton active={viewMode === 'hr'} onClick={() => setViewMode('hr')}>
            <FaUserCheck style={{ color: '#45aaf2' }} /> For HR &amp; Hiring Managers
          </ModeToggleButton>
          <ModeToggleButton active={viewMode === 'client'} onClick={() => setViewMode('client')}>
            <FaBuilding style={{ color: '#00d2d3' }} /> For Contract Clients &amp; Consulting
          </ModeToggleButton>
        </div>

        {/* HERO CONTENT: DYNAMIC HR VS CLIENT */}
        <div style={{ maxWidth: '880px', margin: '0 auto' }}>
          {viewMode === 'hr' ? (
            <>
              <span className="badge badge-gold" style={{ marginBottom: '16px' }}>
                👔 Multi-Skilled Tech Professional • Finland
              </span>
              <h1 style={{
                fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
                fontWeight: '800',
                color: '#ffffff',
                marginBottom: '16px',
                lineHeight: 1.15
              }}>
                Engineering Solid <span style={{ color: '#45aaf2' }}>Software</span>, <span style={{ color: '#00d2d3' }}>Cloud</span> &amp; <span style={{ color: '#2ecc71' }}>Networks</span>
              </h1>
              <p style={{
                fontSize: '1.2rem',
                color: '#cbd5e1',
                lineHeight: 1.6,
                marginBottom: '32px',
                maxWidth: '760px',
                margin: '0 auto 32px auto'
              }}>
                An IT Specialist with an <strong>M.Sc in Circular Economy</strong>, and a <strong>BEng in ICT (Networks and Smart IoT)</strong>, and backed by <strong>skills in AWS, Azure, Cisco, Linux, Python,</strong>.
                Ready to deliver immediate value in Software Engineering, Cloud Infrastructure, Data Analysis, or Network Engineering roles.
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/cv" className="btn-primary">
                  <FaDownload /> Download ATS Resume
                </Link>
                <Link to="/contact" className="btn-secondary">
                  <FaBriefcase /> Offer Full-Time Position
                </Link>
              </div>
            </>
          ) : (
            <>
              <span className="badge badge-green" style={{ marginBottom: '16px' }}>
                💼 Available for Contract Builds &amp; Technical Consulting
              </span>
              <h1 style={{
                fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
                fontWeight: '800',
                color: '#ffffff',
                marginBottom: '16px',
                lineHeight: 1.15
              }}>
                Turn Technical Complexity into <span style={{ color: '#00d2d3' }}>Scalable Digital Solutions</span>
              </h1>
              <p style={{
                fontSize: '1.2rem',
                color: '#cbd5e1',
                lineHeight: 1.6,
                marginBottom: '32px',
                maxWidth: '760px',
                margin: '0 auto 32px auto'
              }}>
                Need a full-stack web application, custom cloud migration on AWS/Azure, secure Cisco network configuration, or data visualization dashboard? I deliver end-to-end technical projects on time and budget.
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/services" className="btn-primary" style={{ background: 'linear-gradient(135deg, #00d2d3 0%, #2ecc71 100%)' }}>
                  Explore Contract Services
                </Link>
                <Link to="/contact" className="btn-secondary">
                  Request a Project Quote
                </Link>
              </div>
            </>
          )}
        </div>
      </HeroContainer>

      {/* METRICS & QUICK FACTS BAR */}
      <MetricsBar>
        <MetricCard>
          <div style={{ background: 'rgba(69, 170, 242, 0.15)', padding: '12px', borderRadius: '12px', color: '#45aaf2', fontSize: '1.8rem' }}>
            <FaGraduationCap />
          </div>
          <div>
            <h4 style={{ margin: 0, color: '#ffffff', fontSize: '1.2rem' }}>Academic Degrees</h4>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem' }}>M.Sc (LUT) , B.Sc ICT (Metropolia) </p>
          </div>
        </MetricCard>

        <MetricCard>
          <div style={{ background: 'rgba(248, 210, 16, 0.15)', padding: '12px', borderRadius: '12px', color: '#f8d210', fontSize: '1.8rem' }}>
            <FaCertificate />
          </div>
          <div>
            <h4 style={{ margin: 0, color: '#ffffff', fontSize: '1.2rem' }}>7+ Certifications</h4>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem' }}>AWS Architect, Cisco CCNA, CyberOps &amp; Red Hat</p>
          </div>
        </MetricCard>

        <MetricCard>
          <div style={{ background: 'rgba(46, 204, 113, 0.15)', padding: '12px', borderRadius: '12px', color: '#2ecc71', fontSize: '1.8rem' }}>
            <FaTools />
          </div>
          <div>
            <h4 style={{ margin: 0, color: '#ffffff', fontSize: '1.2rem' }}>5 Technical Tracks</h4>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem' }}>Software, Cloud, Data, Network &amp; IT Support</p>
          </div>
        </MetricCard>

        <MetricCard>
          <div style={{ background: 'rgba(0, 210, 211, 0.15)', padding: '12px', borderRadius: '12px', color: '#00d2d3', fontSize: '1.8rem' }}>
            <FaGlobe />
          </div>
          <div>
            <h4 style={{ margin: 0, color: '#ffffff', fontSize: '1.2rem' }}>Bilingual Professional</h4>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.88rem' }}>English (Full Prof) • Finnish (Working)</p>
          </div>
        </MetricCard>
      </MetricsBar>

      {/* INTERACTIVE HR & CONTRACT ROLE MATCHER COMPONENT */}
      <section style={{ padding: '40px 24px 80px 24px' }}>
        <HrRoleMatcher />
      </section>

      {/* CORE COMPETENCIES & SERVICES PREVIEW */}
      <section style={{
        padding: '80px 24px',
        backgroundColor: '#050f1d',
        borderTop: '1px solid rgba(69, 170, 242, 0.15)',
        borderBottom: '1px solid rgba(69, 170, 242, 0.15)'
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="badge badge-gold">Capabilities Overview</span>
            <h2 style={{ fontSize: '2.4rem', color: '#ffffff', marginTop: '12px' }}>
              High-Impact Engineering Across 5 Domains
            </h2>
            <p style={{ color: '#94a3b8', maxWidth: '650px', margin: '0 auto', fontSize: '1.05rem' }}>
              Whether you need to fill a full-time engineering vacancy or execute a critical contract project, I bring cross-disciplinary excellence.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '28px'
          }}>
            {/* Service 1: Software */}
            <div className="glass-card" style={{ padding: '32px' }}>
              <div style={{ color: '#45aaf2', fontSize: '2rem', marginBottom: '16px' }}><FaCode /></div>
              <h3 style={{ color: '#ffffff', fontSize: '1.4rem', marginBottom: '8px' }}>Software &amp; Web Development</h3>
              <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '16px' }}>
                Full-stack web applications built using React, Node.js, Express, REST APIs, and Python. Engineered for performance, security, and responsive UI.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>React</span>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>Node.js</span>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>Python</span>
              </div>
              <Link to="/projects" style={{ color: '#45aaf2', fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                View Web Projects <FaArrowRight size={12} />
              </Link>
            </div>

            {/* Service 2: Cloud */}
            <div className="glass-card" style={{ padding: '32px' }}>
              <div style={{ color: '#00d2d3', fontSize: '2rem', marginBottom: '16px' }}><FaCloud /></div>
              <h3 style={{ color: '#ffffff', fontSize: '1.4rem', marginBottom: '8px' }}>Cloud Architecture &amp; Automation</h3>
              <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '16px' }}>
                Architecting secure, cost-optimized infrastructure on AWS and Azure using Terraform (IaC), Docker containers, Kubernetes, and Red Hat OpenShift.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>AWS</span>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>Terraform</span>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>Docker</span>
              </div>
              <Link to="/services" style={{ color: '#00d2d3', fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                View Cloud Services <FaArrowRight size={12} />
              </Link>
            </div>

            {/* Service 3: Data */}
            <div className="glass-card" style={{ padding: '32px' }}>
              <div style={{ color: '#f8d210', fontSize: '2rem', marginBottom: '16px' }}><FaDatabase /></div>
              <h3 style={{ color: '#ffffff', fontSize: '1.4rem', marginBottom: '8px' }}>Data Analytics &amp; Visualization</h3>
              <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '16px' }}>
                Data processing, statistical analysis (SPSS), SQL database design, and real-time visualization dashboards with Python and Grafana.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>Python</span>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>SQL</span>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>Grafana</span>
              </div>
              <Link to="/services" style={{ color: '#f8d210', fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                View Analytics Offerings <FaArrowRight size={12} />
              </Link>
            </div>

            {/* Service 4: Network */}
            <div className="glass-card" style={{ padding: '32px' }}>
              <div style={{ color: '#2ecc71', fontSize: '2rem', marginBottom: '16px' }}><FaNetworkWired /></div>
              <h3 style={{ color: '#ffffff', fontSize: '1.4rem', marginBottom: '8px' }}>Network Setup &amp; Security</h3>
              <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '16px' }}>
                Cisco-certified network routing, switching, VLAN segmentation, VPN setups, firewall rules, and CyberOps threat mitigation.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>Cisco CCNA</span>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>CyberOps</span>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>Firewalls</span>
              </div>
              <Link to="/services" style={{ color: '#2ecc71', fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                View Network Services <FaArrowRight size={12} />
              </Link>
            </div>

            {/* Service 5: IT & Systems */}
            <div className="glass-card" style={{ padding: '32px' }}>
              <div style={{ color: '#9b59b6', fontSize: '2rem', marginBottom: '16px' }}><FaTools /></div>
              <h3 style={{ color: '#ffffff', fontSize: '1.4rem', marginBottom: '8px' }}>IT Systems &amp; Technical Support</h3>
              <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '16px' }}>
                Linux sysadmin, Smart IoT device integration (Raspberry Pi), technical troubleshooting, customer care SLA compliance, and system hardening.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>Linux</span>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>IoT</span>
                <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: '12px' }}>Helpdesk</span>
              </div>
              <Link to="/services" style={{ color: '#9b59b6', fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                View IT Support Offerings <FaArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CALL TO ACTION FOR HR & CLIENTS */}
      <section style={{ padding: '80px 24px', textAlign: 'center' }}>
        <div className="glass-card" style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '48px 32px',
          background: 'linear-gradient(135deg, rgba(16, 37, 66, 0.9) 0%, rgba(7, 19, 37, 0.95) 100%)',
          borderColor: 'rgba(69, 170, 242, 0.4)'
        }}>
          <h2 style={{ fontSize: '2.2rem', color: '#ffffff', marginBottom: '16px' }}>
            Ready to Connect for Full-Time Roles or Contract Projects?
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '1.1rem', maxWidth: '640px', margin: '0 auto 32px auto' }}>
            Let’s discuss how my technical capabilities and academic background can bring immediate value to your engineering team or upcoming project.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary">
              <FaCheckCircle /> Get in Touch / Send Message
            </Link>
            <Link to="/cv" className="btn-secondary">
              <FaDownload /> Download Complete CV (PDF)
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}

export default HomePage;
